import React, { useState, useEffect, useContext } from "react";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import Modal from "common/Modal";
import { getIndexes } from "common/utils";
import SelectAutocomplete from "common/SelectAutocomplete";
import styled from "styled-components";
import Loading from "common/Loading";

const LoadingWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 30px;
`;

const SelectorContainer = styled.div`
  width: 80%;
`;

const ConfirmationWrapper = styled.div`
  margin-top: 25px;
  h4 {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    i {
      font-size: 22px;
      margin-right: 10px;
      margin-bottom: 5px;
    }
  }
`;

const ErrorMessage = styled.span`
  text-align: left;
`;

const parseTimezones = (input) => {
  const [header, ...rows] = input.trim().split("\n");
  const headerIndex = getIndexes(header);
  return rows.map((row) => {
    const rowAttrs = row.trim().split(/\s+/);
    const country = rowAttrs[headerIndex["COUNTRY"]];
    const timezone = rowAttrs[headerIndex["TIMEZONE"]];
    return { value: timezone, label: `${country} ${timezone}` };
  });
};

const TimeZoneSelector = ({ timezone, setTimezone }) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const doLoad = () =>
        ifCl
          .run("show system date timezone")
          .then(parseTimezones)
          .then((options) => {
            setOptions(options);
          });
    doLoad();
  }, []);

  return (
    <SelectorContainer>
      <SelectAutocomplete
        label="Time Zone"
        name="timezone"
        selected={timezone}
        options={options}
        onChange={(value) => setTimezone(value)}
      />
    </SelectorContainer>
  );
};

const TimeZoneModal = () => {
  const [open, setOpen] = useState(false);
  const actions = useContext(ActionsContext);
  const [timezone, setTimezone] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [timezoneSelected, setTimezoneSelected] = useState("");
  const [errorFooter, setErrorFooter] = useState("");
  const [bqn, setBqn] = useState("");
  const [loading, setLoading] = useState(false);
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  useEffect(
    () =>
      actions.recv("set-timezone", (value) => {
        const { timezone, bqn } = value;
        setBqn(bqn);
        doOpen();
        setTimezone(timezone);
      }),
    []
  );
  const doOpen = () => {
    setShowConfirm(false);
    setTimezoneSelected("");
    setErrorFooter("");
    setOpen(true);
    setLoading(false);
  };
  const doClose = () => setOpen(false);

  const doApply = () => {
    if (!showConfirm) {
      setShowConfirm(true);
    } else {
        setLoading(true);
      ifCl
        .run(
          `system date timezone ${timezoneSelected} bqn-id ${
            mode === "cluster" ? bqn : bqnId
          }`
        )
        .then((response) => {
            setLoading(false);
          const indexError = response.indexOf("%ERR");
          if (indexError !== -1) {
            const error = response.slice(indexError, response.length);
            throw Error(error);
          } else {
            doClose();
          }
        })
        .catch((error) => {
            setErrorFooter(`Error: ${error}`)
            setLoading(false);
        });
    }
  };

  const handleTimeZone = (value) => {
    setTimezoneSelected(value);
  };

  if (!open) return null;
  return (
    <Modal
      title={`Set Time Zone${bqn ? ` for BQN ${bqn}` : ""}`}
      superIcon="edit"
      onApply={doApply}
      closeLabel={!errorFooter ? "CANCEL" : loading ? null : "CLOSE"}
      onClose={doClose}
      applyLabel={!errorFooter && timezoneSelected && !loading ? "APPLY" : null}
      content={() => {
        return (
          <>
            <TimeZoneSelector
              timezone={timezone}
              setTimezone={handleTimeZone}
            />
            {showConfirm && !errorFooter && !loading ? (
              <ConfirmationWrapper>
                <h4>
                  <i className="material-icons">info</i> Confirm time zone
                  change?
                </h4>
                <div>
                  Changing the time by more than a few minutes may cause a
                  disruption in traffic
                  of 10 seconds to 2 minutes, depending on the number of
                  interfaces, CPU cores, and memory size.
                  In any case, after a time change, it is
                  recommended to perform a software reload of the active bqn
                  package in Administration &gt; Software.
                </div>
              </ConfirmationWrapper>
            ) : null}
            {loading ?<LoadingWrapper><Loading /></LoadingWrapper>:null}
          </>
        );
      }}
      footerMessage={
        errorFooter !== "" ? (
          <ErrorMessage className="color-red">
            <h5>{errorFooter}</h5>
          </ErrorMessage>
        ) : null
      }
    />
  );
};

export default TimeZoneModal;
